import { EventCategory, EventAction } from "../BNGoogleTagManager";
import { DataLayerEvent } from "./DataLayerEvent";

export class PublicPortalDataLayerEvent extends DataLayerEvent {
  category: EventCategory;
  action: EventAction;
  label?: string;
  applicationId?: number;
  creditLimit?: number;

  constructor(
    category: EventCategory,
    action: EventAction,
    label?: string,
    applicationId?: number,
    creditLimit?: number,
    isOneFlow?: boolean,
  ) {
    super({
      eventName: mapCategoryToGa4EventName(category, action, isOneFlow),
      data: {
        checktype_name: action.toString(),
        application_id: applicationId ? applicationId?.toString() : undefined,
        checktype_value: label,
        credit_limit: creditLimit,
      },
    });
    this.category = category;
    this.action = action;
    this.label = label;
    this.applicationId = applicationId;
  }

  /**
   * A factory method with named parameters.
   * Allows instantiating the class in a way that is easier to read.
   */
  static fromObject(obj: {
    category: EventCategory;
    action: EventAction;
    label?: string;
    applicationId?: number;
    creditLimit?: number;
    isOneFlow?: boolean;
  }): PublicPortalDataLayerEvent {
    return new PublicPortalDataLayerEvent(
      obj.category,
      obj.action,
      obj.label,
      obj.applicationId,
      obj.creditLimit,
      obj.isOneFlow,
    );
  }

  override toGaObject(): object {
    const eventToPush = super.toGaObject();

    eventToPush["eventCategory"] = this.category;
    eventToPush["eventAction"] = this.action;

    if (this.label) {
      eventToPush["eventLabel"] = this.label;
    }
    if (this.applicationId) {
      eventToPush["ApplicationId"] = this.applicationId;
    }

    return eventToPush;
  }
}

const mapCategoryToGa4EventName = (x: EventCategory, action: EventAction, isOneFlow?: boolean): string => {
  switch (x) {
    case EventCategory.GreenLoanApplication:
      return "my_gl_application";
    case EventCategory.MyPage:
      return "MyPage";
    case EventCategory.LoanApplication:
      return isOneFlow ? "my_ul_application_oneflow" : "my_ul_application";
    case EventCategory.LoanExtensionApplication:
      return "my_le_application";
    case EventCategory.LoanCustomPaymentApplication:
      return "my_ulcpp_application";
    case EventCategory.CreditPurchaseApplication:
      return "my_cp_application";
    case EventCategory.TopUpApplication:
      return "my_tu_application";
    case EventCategory.MyPageCreditCard:
      return "MyPageCreditCard";
    case EventCategory.MyPageCreditCardTransactions:
      return "MyPageCreditCardTransactions";
    case EventCategory.SavingsAccountApplication:
      return "my_sa_application";
    case EventCategory.SavingsAccountPlusApplication:
      return "my_sw_application";
    case EventCategory.SavingsAccountDelayedWithdrawalApplication:
      return "my_sd_application";
    case EventCategory.SavingsAccountFixedTerm12Application:
      return "my_sf12_application";
    case EventCategory.SavingsAccountFixedTerm24Application:
      return "my_sf24_application";
    case EventCategory.CreditCardApplication:
      switch (action) {
        case EventAction.Received:
          return "cc_application_received";
        case EventAction.Approved:
          return "cc_application_approved";
        case EventAction.Rejected:
          return "cc_application_rejected";
      }
      return "my_cc_application";
    case EventCategory.CreditCardLimitIncreaseApplication:
      switch (action) {
        case EventAction.Apply:
          return "ccli_application_received";
        case EventAction.Approved:
          return "ccli_application_approved";
        case EventAction.Rejected:
          return "ccli_application_rejected";
        case EventAction.Buffered:
          return "ccli_application_buffered";
        case EventAction.Error:
          return "ccli_application_error";
      }
      return "my_ccli_application";
    case EventCategory.Insurance:
      return "Insurance";
    case EventCategory.CreditLineApplication:
      switch (action) {
        case EventAction.Received:
          return "cl_application_received";
        case EventAction.Approved:
          return "cl_application_approved";
        case EventAction.Rejected:
          return "cl_application_rejected";
      }
      return "my_cl_application";
    case EventCategory.CreditLineLimitIncreaseApplication:
      switch (action) {
        case EventAction.Apply:
          return "clli_application_received";
        case EventAction.Approved:
          return "clli_application_approved";
        case EventAction.Rejected:
          return "clli_application_rejected";
        case EventAction.Buffered:
          return "clli_application_buffered";
        case EventAction.Error:
          return "clli_application_error";
      }
      return "my_clli_application";
    case EventCategory.RefinanceEditor:
      switch (action) {
        case EventAction.Received:
          return "MyPageRefinanceEditor_received";
        case EventAction.Approved:
          return "MyPageRefinanceEditor_approved";
        case EventAction.Rejected:
          return "MyPageRefinanceEditor_rejected";
      }
      return "MyPageRefinanceEditor";
  }
  return "unknown";
};
